import { useEffect, useRef } from 'react';
import Image from 'next/image';
import cn from 'classnames';
import MarkdownCopy from '@components/cms/Slices/MarkdownCopy';
import Masonry from 'react-masonry-css';
import { PhotoGridSlice } from '@adapters/prismic/types/brandedStorePage';
import YouTubeEmbed from '@components/cms/Slices/MediaEmbed/YouTubeEmbed';
import Button from '@components/ui/Button';
import Container from '@components/global/Container';
import {
  trackPromotionEvent,
  PromotionEventName,
  InteractionLocation,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { buildCreativeSlotName, determinePageType } from '@utils/gtm/ga4/utils';

import styles from './PhotoGrid.module.scss';
import useObjectVisibility from '@hooks/useObjectVisibility';

export const PhotoGrid = ({
  locale,
  items,
  primary,
  brandedStoreCoopAttribute,
  sliceIndex,
}: PhotoGridSlice) => {
  const breakpointColumns = {
    700: 2,
    500: 1,
  };

  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useObjectVisibility(ref);

  useEffect(() => {
    if (typeof window !== 'undefined' && items?.length && isVisible) {
      items.forEach((item, index) => {
        const extractedText = item?.copy?.[0]?.text
          ?.split('\n')[0]
          ?.replace(/[#*_`~]/g, '') // Remove markdown characters
          ?.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '') // Remove punctuation
          ?.trim(); // Remove extra spaces
        const isVideo = item.format === 'video';
        trackPromotionEvent({
          eventName: PromotionEventName.viewPromotion,
          page: {
            path: window.location.pathname,
            page_type: determinePageType(),
          },
          interaction: {
            coop_brand: brandedStoreCoopAttribute || null,
            interaction_location: InteractionLocation.PhotoGrid,
          },
          ecommerce: {
            creative_name: `${item.format},${
              isVideo ? item?.video_id : item?.image?.url
            }`,
            creative_slot: buildCreativeSlotName({
              sliceIndex,
              interactionLocation: InteractionLocation.PhotoGrid,
              itemIndex: index,
            }),
            promotion_id:
              extractedText ??
              (isVideo ? item?.video_id : item?.image?.alt) ??
              null,
            promotion_name: item?.image?.alt || null,
            isVideo: item.format === 'video',
          },
        });
      });
    }
  }, [items, brandedStoreCoopAttribute, sliceIndex, isVisible]);

  return (
    <Container>
      <div className={styles.photoGridContainer} id={primary?.id} ref={ref}>
        <Masonry
          breakpointCols={breakpointColumns}
          className={styles.masonryGrid}
          columnClassName={styles.masonryGridColumn}
        >
          {items?.map((item, i) => (
            <article
              key={`photoGrid-card-${i}`}
              className={cn(styles.cardContainer, {
                [styles.positionLeft]: item.position === 'Left',
                [styles.positionRight]: item.position === 'Right',
                [styles.copyLeft]: item.copy_position === 'left',
              })}
            >
              <div
                style={
                  item.format === 'image'
                    ? {
                        aspectRatio: `auto ${item?.image?.dimensions?.width} / ${item?.image?.dimensions?.height}`,
                      }
                    : undefined
                }
                className={cn(styles.imageContainer, {
                  [styles.copyLeft]: item.copy_position === 'left',
                })}
              >
                {item.format === 'image' ? (
                  <Image
                    src={item?.image?.url}
                    alt={item.image.alt || ''}
                    fill
                    sizes="100vw"
                  />
                ) : (
                  <YouTubeEmbed
                    html={`<iframe width="100%" height="100%" src="https://www.youtube.com/embed/${item?.video_id}?feature=oembed&enablejsapi=1&controls=1&autoplay=0&loop=1&mute=1&playsinline=1&modestbranding=1&rel=0&autohide=1" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`}
                    dimensions={{
                      height: item.video_height,
                      width: item.video_width,
                    }}
                  />
                )}
              </div>
              {item.copy?.[0]?.text && (
                <div
                  className={cn(styles.copy, {
                    [styles.copyLeft]: item.copy_position === 'left',
                    [styles.copyIndentLeft]: item.copy_indent === 'left',
                    [styles.copyIndentRight]: item.copy_indent === 'right',
                    [styles.copyIndentCenter]: item.copy_indent === 'centre',
                  })}
                >
                  <MarkdownCopy
                    text={item?.copy?.[0]?.text ?? ''}
                    className={styles.copyText}
                  />
                </div>
              )}
            </article>
          ))}
        </Masonry>
        {primary?.cta_label && primary?.cta_importance && primary?.cta_url && (
          <Button
            label={primary.cta_label}
            size="large"
            variant={primary.cta_importance}
            type="button"
            href={`/${locale}${primary.cta_url}`}
            handleClick={() => {
              trackPromotionEvent({
                eventName: PromotionEventName.selectPromotion,
                page: {
                  path: window.location.pathname,
                  page_type: determinePageType(),
                },
                interaction: {
                  coop_brand: brandedStoreCoopAttribute || null,
                  interaction_location: InteractionLocation.PhotoGrid,
                },
                ecommerce: {
                  creative_name: `link,/${locale}${primary.cta_url}`,
                  creative_slot: buildCreativeSlotName({
                    sliceIndex,
                    interactionLocation: InteractionLocation.PhotoGrid,
                    itemIndex: items?.length,
                  }),
                  promotion_id: primary.cta_label ?? null,
                  promotion_name: primary.cta_label ?? null,
                  isVideo: false,
                },
              });
            }}
          />
        )}
      </div>
    </Container>
  );
};

export default PhotoGrid;
